import { useMemo, useState, useEffect, useRef } from 'react';

import { difference, union } from 'ramda';
import { useController } from 'react-hook-form';

import { ETripEvent } from 'detailsv2/structure';
import { camelize } from 'utils/humps';

import { FavoriteIconsKey, FavoriteIconsKeySnaked } from '../../../../../types';
import { useFavoriteIcons, useUpdateFavoriteIcon } from '../../../../../useFavoriteIcons';
import { openIconSearch } from '../IconSearch/functions';
import { DEFAULT_ICONS } from './constants';
import { IIconPicker } from './types';

const useIconPicker = ({ name, control, eventType, transportationType }: IIconPicker) => {
  const isTransportationTypeChanged = useRef(false);
  const isFavoriteIconChanged = useRef(false);
  const snakedEventType =
    eventType === ETripEvent.Transfer && transportationType
      ? (`${eventType}_${transportationType}` as FavoriteIconsKeySnaked)
      : eventType;

  const camelizedEventType = camelize(snakedEventType) as FavoriteIconsKey;

  const { data } = useFavoriteIcons();
  const [favoriteIconsList, setFavoriteIconsList] = useState<string[]>(
    data?.[camelizedEventType]?.filter(Boolean) || [],
  );

  // Reset favorite icons list when transportation type changes
  useEffect(() => {
    // Prevent resetting favorite icons list if transportation type was not changed
    if (!isTransportationTypeChanged.current) return;
    setFavoriteIconsList(data?.[camelizedEventType]?.filter(Boolean) || []);
  }, [data, camelizedEventType]);

  // Reset favorite icon changed flag when transportation type changes
  useEffect(() => {
    isFavoriteIconChanged.current = false;
  }, [transportationType]);

  const mutation = useUpdateFavoriteIcon();
  const defaultIcons = (() => {
    if (eventType === ETripEvent.Transfer) {
      if (!transportationType) return [];
      return DEFAULT_ICONS[eventType][transportationType];
    }
    return DEFAULT_ICONS[eventType] || [];
  })();

  const {
    field: { value, onChange },
  } = useController({ name: name, control: control, defaultValue: favoriteIconsList[0] || defaultIcons[0] });

  // State for by default or saving icon from search
  const [cachedIcon, setCachedIcon] = useState<string>(value);

  useEffect(() => {
    // If favorite icon was changed, we don't need to reset icon
    if (isFavoriteIconChanged.current) return;

    // If transportation type was changed, we need to reset icon
    if (!isTransportationTypeChanged.current) {
      isTransportationTypeChanged.current = true;
      return;
    }

    if (eventType === ETripEvent.Transfer) {
      const newDefaultIcon = favoriteIconsList[0] || defaultIcons[0];
      onChange(newDefaultIcon);
      setCachedIcon(newDefaultIcon);
    }
  }, [transportationType, favoriteIconsList]);

  const sortedIcons = useMemo(() => {
    // Icon list should have no duplicates
    const iconsWithCached = union([cachedIcon], defaultIcons);

    if (favoriteIconsList.length > 0) {
      // Favorite icons should be always in the first place when icons get sorted
      return [...favoriteIconsList, ...difference(iconsWithCached, favoriteIconsList)];
    }

    return iconsWithCached;
    // Icons should be sorted only if it is the first render or if we're getting new icon from search
  }, [cachedIcon]);

  // Update favorite icons state when we get new default data from API
  const isIconRendered = sortedIcons.includes(value);
  useEffect(() => {
    if (!isIconRendered) setCachedIcon(value);
  }, [value, transportationType]);

  const handleFavoriteIcon = (icon: string) => {
    // TODO change after multiple favorites feature
    setFavoriteIconsList((prev) => (prev.includes(icon) ? prev.filter((el) => el !== icon) : [icon]));
    mutation.mutate({ eventType: snakedEventType, icon: favoriteIconsList.includes(icon) ? null : icon });
    isFavoriteIconChanged.current = true;
  };

  const handleIconSearchModal = async () => {
    const result = await openIconSearch();

    if (!result) return; // If we click "back" instead of select

    if (result.isFavorite) {
      handleFavoriteIcon(result.selectedIcon);
    }
    setCachedIcon(result.selectedIcon);
    onChange(result.selectedIcon);
  };

  return { value, onChange, sortedIcons, handleFavoriteIcon, favoriteIconsList, handleIconSearchModal };
};

export default useIconPicker;
